import { useCallback, useState } from "react"

const scrollIntoViewWhenReady = (
  elementId: string,
  options: ScrollIntoViewOptions,
  attemptsLeft = 10,
) => {
  const element = document.getElementById(elementId)
  if (element) {
    element.scrollIntoView(options)
    return
  }

  if (attemptsLeft <= 0) return
  requestAnimationFrame(() => {
    scrollIntoViewWhenReady(elementId, options, attemptsLeft - 1)
  })
}

export const useQuestionSelection = () => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<
    string | undefined
  >(undefined)
  const [selectedBoxId, setSelectedBoxId] = useState<string | undefined>(
    undefined,
  )

  const clearSelection = useCallback(() => {
    setSelectedQuestionId(undefined)
    setSelectedBoxId(undefined)
  }, [])

  const selectAndScroll = useCallback((questionId: string, boxId?: string) => {
    const targetBoxId = boxId || questionId
    setSelectedQuestionId(questionId)
    setSelectedBoxId(targetBoxId)

    scrollIntoViewWhenReady(`question-${questionId}`, {
      behavior: "smooth",
      block: "center",
    })
    scrollIntoViewWhenReady(String(targetBoxId), {
      behavior: "smooth",
      block: "center",
    })
  }, [])

  const handleBoxSelect = useCallback(
    (id: string) => {
      const qId = id.split(",")[0]
      if (selectedQuestionId !== qId) {
        setSelectedQuestionId(qId)
      }
      if (selectedBoxId !== id) {
        setSelectedBoxId(id)
      }

      scrollIntoViewWhenReady(`question-${qId}`, {
        behavior: "smooth",
        block: "center",
      })
    },
    [selectedBoxId, selectedQuestionId],
  )

  const handleInputFocus = useCallback(
    (questionId: string, optionBoxId?: string) => {
      if (selectedQuestionId !== questionId) {
        setSelectedQuestionId(questionId)
      }
      const targetBoxId = optionBoxId || questionId
      if (selectedBoxId !== targetBoxId) {
        setSelectedBoxId(targetBoxId)
      }

      scrollIntoViewWhenReady(String(targetBoxId), {
        behavior: "smooth",
        block: "center",
      })
    },
    [selectedBoxId, selectedQuestionId],
  )

  return {
    selectedQuestionId,
    selectedBoxId,
    handleBoxSelect,
    handleInputFocus,
    selectAndScroll,
    clearSelection,
  }
}
